import {
  Box,
  Divider,
  Flex,
  Heading,
  Icon,
  Link,
  Spacer,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react'
import moment from 'moment'
import numeral from 'numeral'
import type { ReactElement } from 'react'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { NavLink } from 'react-router-dom'
import type { CellProps, Column } from 'react-table'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import {
  checkPermission,
  currencySymbol,
  PermissionsEnum,
} from '../../../utils'
import type { InvoiceResponse } from '../../interfaces'
import { UserContext } from '../../providers'
import Pagination from '../Pagination'
import SearchBar from '../SearchBar'
import SupportRequest from './SupportRequestModal'
import useToast from '../../hooks/useToast'

interface InvoiceStatusWithColorColumnProps {
  color: string
  value: string
}

const InvoicesTable = ({ minimal }: { minimal?: boolean }): ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const hasBillingWritePermission = useMemo(
    () =>
      checkPermission({
        user,
        permission: PermissionsEnum.SELF_BILLING_MANAGE,
      }),
    [user],
  )

  const [data, setData] = useState<InvoiceResponse[]>()

  const { request, loading } = useFetch<InvoiceResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const toast = useToast()

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const response = await request.get(
        `/api/clients/${user.clientNumber}/invoice`,
      )

      if (request.error) {
        toast({
          description: t(
            'error_fetching_data',
            'Error in fetching data. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }

  useEffect(() => {
    void getData()
  }, [user])

  const tableData = useMemo<InvoiceResponse[]>(() => data ?? [], [data])

  const columns = useMemo<Array<Column<InvoiceResponse>>>(() => {
    const cols = [
      {
        Header: t('created_on', 'Created On').toString(),
        accessor: values =>
          moment(values.invoice_date).format('DD MMMM YYYY, hh:mm A'),
      },
      {
        Header: t('due_on', 'Due On').toString(),
        accessor: values =>
          moment(values.due_date).format('DD MMMM YYYY, hh:mm A'),
        isHidden: minimal,
      },
      {
        Header: t('amount', 'Amount').toString(),
        accessor: ({ displayCurrency, totalWithTax, totalWithTaxInINR }) => {
          const symbol = currencySymbol(displayCurrency)
          const displayAmount =
            displayCurrency === 'INR' ? totalWithTaxInINR : totalWithTax

          const formattedAmount = numeral(displayAmount).format('0,0.00')

          return `${symbol} ${formattedAmount}`
        },
      },
      {
        Header: t('status', 'Status').toString(),
        accessor: ({ status: innerStatus, due_date_distance }) => {
          if (innerStatus === 'due') {
            const postfix =
              due_date_distance === 1 ? t('day', 'day') : t('days', 'days')
            if (due_date_distance < 0) {
              return {
                value: `${t('due_in', 'Due in')} ${Math.abs(
                  due_date_distance,
                )} ${postfix}`,
                color: colors.appYellowOrange,
              }
            }
            return {
              value: `${t('overdue_since', 'Overdue since')} ${Math.abs(
                due_date_distance,
              )} ${postfix}`,
              color: colors.appRed,
            }
          }

          return { value: t('paid', 'Paid'), color: colors.appGreenishTeal }
        },
        Cell: ({
          value: { color, value },
        }: CellProps<InvoiceResponse, InvoiceStatusWithColorColumnProps>) => (
          <Box as="span" color={color}>
            {String(value)}
          </Box>
        ),
      },
      {
        Header: 'Action',
        disableSortBy: true,
        accessor: ({
          status: innerStatus,
          canPayOnline,
          hosted_invoice_url
        }) => {
          if (innerStatus !== 'due') {
            return {
              msg: t('no_action_required', 'No action required')
            }
          }
          if (canPayOnline === false) {
            return {
              msg: "Download invoice and follow the payment instructions."
            }
          }
          if (canPayOnline === true && hosted_invoice_url) {
            return {
              hosted_invoice_url
            };
          }
          return {
            msg: ""
          }
        },
        Cell: ({ value }: CellProps<InvoiceResponse, any>) => {
          const { hosted_invoice_url, msg } = value
          if (hosted_invoice_url) {
            return (
              <Link href={hosted_invoice_url} isExternal>
                Pay online
              </Link>
            )
          }
          return msg
        },
        isHidden: minimal ?? !hasBillingWritePermission,
      },
      {
        Header: t('view', 'View').toString(),
        disableSortBy: true,
        accessor: ({ id, stripe_invoice_id }) => ({ 
          invoiceId: id, 
          hasStripeInvoice: !!stripe_invoice_id
        }),
        Cell: ({ value }: CellProps<InvoiceResponse, any>) => {
          const { invoiceId, hasStripeInvoice } = value;
          
          // Show download link only if we have a Stripe invoice
          if (hasStripeInvoice && user?.clientNumber) {
            const downloadUrl = `/api/clients/${user.clientNumber}/invoice/${invoiceId}/invoice.pdf`;
            return (
              <Link href={downloadUrl} isExternal>
                Download invoice
              </Link>
            );
          }
          
          return '';
        },
      },
    ]
    return cols.filter(col => !col.isHidden) as any
  }, [minimal, hasBillingWritePermission])

  const table = useTable<InvoiceResponse>(
    { columns, data: tableData },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state: { pageIndex, pageSize, ...tableState },
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    setGlobalFilter,
    setPageSize,
    previousPage,
    nextPage,
    gotoPage,
  } = table

  const { isOpen, onOpen, onClose } = useDisclosure()

  const pageOffset = pageIndex * pageSize
  return (
    <>
      {!minimal ? (
        <Flex align="center">
          <SearchBar
            searchQuery={tableState.globalFilter as string}
            setSearchQuery={setGlobalFilter}
            padding="1em 0em"
            minWidth="30em"
          />
          <Spacer />
          {rows.length ? (
            <Text fontWeight="500">
              {t('showing', 'Showing')} {pageOffset + (pageCount === 0 ? 0 : 1)}{' '}
              {t('to', 'to')}{' '}
              {pageOffset + pageSize <= rows.length
                ? pageOffset + pageSize
                : rows.length}{' '}
              {t('of', 'of')} {rows.length} {t('entries', 'entries')}
            </Text>
          ) : null}
        </Flex>
      ) : null}

      <Table {...getTableProps} size="md">
        <Thead bgColor="appPaleGreyTwo">
          {headerGroups.map(headerGroup => {
            const { key, ...restHeaderGroupProps } =
              headerGroup.getHeaderGroupProps()
            return (
              <Tr key={key} {...restHeaderGroupProps}>
                {headerGroup.headers.map(column => {
                  const { key: keyCol, ...restColumn } = column.getHeaderProps(
                    column.getSortByToggleProps(),
                  )
                  return (
                    <Th key={keyCol} {...restColumn}>
                      {column.render('Header')}
                      {!column.disableSortBy ? (
                        <Box as="span" pl="2" color={colors.appBattleshipGrey}>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon
                                as={FaSortUp}
                                aria-label={t(
                                  'sorted_descending',
                                  'sorted descending',
                                )}
                              />
                            ) : (
                              <Icon
                                as={FaSortDown}
                                aria-label={t(
                                  'sorted_ascending',
                                  'sorted ascending',
                                )}
                              />
                            )
                          ) : (
                            <Icon
                              as={FaSort}
                              aria-label={t('sort_by_column', 'sort by column')}
                            />
                          )}
                        </Box>
                      ) : null}
                    </Th>
                  )
                })}
              </Tr>
            )
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {!loading ? (
            rows.length ? (
              page.map(row => {
                prepareRow(row)
                const { key, ...restRowProps } = row.getRowProps()
                return (
                  <Tr key={key} {...restRowProps}>
                    {row.cells.map(cell => {
                      const { key: keyRow, ...restCellProps } =
                        cell.getCellProps()
                      return (
                        <Td key={keyRow} {...restCellProps}>
                          <Text>{cell.render('Cell')}</Text>
                        </Td>
                      )
                    })}
                  </Tr>
                )
              })
            ) : (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  <Text>No invoices available</Text>
                </Td>
              </Tr>
            )
          ) : (
            <Tr>
              <Td colSpan={6} textAlign="center">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="appPaleGrey"
                  color="appBlue"
                  size="xl"
                />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      {!minimal ? (
        <>
          {pageOptions.length > 0 ? <Divider mt={4} mb={4} /> : <Box mb={4} />}
          <Flex justifyContent="space-between" alignItems="center">
            <Text>
              {t(
                'invoice_queries_prefix',
                'For queries regarding invoices, please ',
              )}
              {user?.pricingPlan.isPaidPlan ? (
                <Link
                  fontSize="1em"
                  onClick={() => {
                    window.Intercom(
                      'showNewMessage',
                      t(
                        'invoice_support_initial_text',
                        'Hi\n\nI have a query regarding my invoice.',
                      ).toString(),
                    )
                  }}
                >
                  {t('contact_us', 'contact us')}.
                </Link>
              ) : (
                <Link onClick={onOpen}>{t('contact_us', 'contact us')}</Link>
              )}
            </Text>
            <Pagination
              pageIndex={pageIndex}
              pageSize={pageSize}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageCount={pageCount}
              pageOptions={pageOptions}
              setPageSize={setPageSize}
              previousPage={previousPage}
              nextPage={nextPage}
              gotoPage={gotoPage}
            />
          </Flex>
          <SupportRequest isOpen={isOpen} onClose={onClose} />
        </>
      ) : (
        <Flex justifyContent="space-between" alignItems="center">
          <Text>
            <Link as={NavLink} to="/invoices">
              {t('view_all_invoices', 'Click here to view all invoices')}
            </Link>
          </Text>
        </Flex>
      )}
    </>
  )
}

export default InvoicesTable
